import React, { useContext, useEffect, useState } from 'react';
import {
    Modal, ModalHeader, ModalBody, Form, Row, Col,
    FormGroup, Label, Input, Button, InputGroup
} from 'reactstrap';
import Select from 'react-select';
import Swal from 'sweetalert2';

import { AgendaContext } from '../../context/AgendaContext';
import { TipoAgendaContext } from '../../context/TipoAgendaContext';
import { TerceiroContext } from '../../context/TerceiroContext';
import { AuthContext } from '../../context/AuthContext';
import { CursoContext } from '../../context/CursoContext';
import { CreateAgendaType } from '../../context/hooks/useAgenda';
import { TerceiroType } from '../../context/hooks/useTerceiro';
import { CurrencyInput } from '../Form/CurrencyInput';

import CreateTipoAgendaModal from '../TipoAgenda/CreateTipoAgendaModal';

interface Props {
    isOpen: boolean;
    toggle: () => void;
    defaultStart?: string;
    defaultEnd?: string;
    contratoId?: number | null;
    onCreated?: () => void;
}

type ServicoDoTerceiro = {
    servicoId: number;
    servicoName: string;
    value: number;
};

type SelectedTerceiro = {
    terceiro: TerceiroType;
    servicos: ServicoDoTerceiro[];
    servicoId?: number | null;
    valorServico?: number | null;
};

const getEmptyCreateForm = (
    empresaId: number,
    start = '',
    end = '',
    contratoId: number | null = null
): CreateAgendaType => ({
    description: '',
    location: '',
    responsible: '',
    beca: '',
    startDate: start,
    endDate: end,
    tipoAgendaId: 0,
    empresaId,
    contratoId,
    cursoIds: [],
    terceiros: [],
});

type Option = { value: number; label: string };

export const CreateAgendaModal: React.FC<Props> = ({
    isOpen,
    toggle,
    defaultStart = '',
    defaultEnd = '',
    contratoId = null,
    onCreated
}) => {
    const { objUserTk } = useContext(AuthContext);
    const { createAgenda, checkConflitoHorarioFotografo } = useContext(AgendaContext);
    const { listTipoAgenda, getAllTipoAgendaPaged } = useContext(TipoAgendaContext);
    const { listTerceiro, getTerceiroPaged, handleGetServicosByTerceiro } = useContext(TerceiroContext);
    const { handleGetCursoByEmpresaId, listCurso } = useContext(CursoContext);

    const [createModalOpen, setCreateModalOpen] = useState(false);

    const [form, setForm] = useState<CreateAgendaType>(
        getEmptyCreateForm(objUserTk.EmpresaId, defaultStart, defaultEnd, contratoId)
    );

    const [selected, setSelected] = useState<SelectedTerceiro[]>([]);

    useEffect(() => {
        if (isOpen) {
            setForm(getEmptyCreateForm(objUserTk.EmpresaId, defaultStart, defaultEnd, contratoId));
            setSelected([]);

            if (!listTipoAgenda.length) getAllTipoAgendaPaged(1, 1000, objUserTk.EmpresaId);
            if (!listTerceiro.length) getTerceiroPaged(1, '', objUserTk.EmpresaId, 1000, 'Fotógrafo');
            handleGetCursoByEmpresaId(objUserTk.EmpresaId);
        }
    }, [isOpen]);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const onSelectTerceiro = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const id = Number(e.target.value);
        if (!id) return;

        const already = selected.find(s => s.terceiro.id === id);
        if (already) {
            setSelected(prev => prev.filter(s => s.terceiro.id !== id));
            return;
        }

        const terceiro = listTerceiro.find(t => t.id === id);
        if (!terceiro) return;

        const conflito = await checkConflitoHorarioFotografo(id, form.startDate, form.endDate);
        if (conflito) return;

        const servicos = (await handleGetServicosByTerceiro(id)) ?? [];
        const first = servicos.length ? servicos[0].servicoId : null;
        const firstValue = servicos.length ? servicos[0].value : null;

        setSelected(prev => [
            ...prev,
            { terceiro, servicos, servicoId: first, valorServico: firstValue ?? null }
        ]);
    };

    const onChangeServico = (terceiroId: number, servicoId: number | null) => {
        setSelected(prev =>
            prev.map(s => {
                if (s.terceiro.id !== terceiroId) return s;
                const valueFromService =
                    servicoId != null
                        ? (s.servicos.find(ss => ss.servicoId === servicoId)?.value ?? null)
                        : null;
                return { ...s, servicoId, valorServico: valueFromService };
            })
        );
    };

    const listCursoOptions: Option[] = listCurso.map(curso => ({
        value: curso.id,
        label: curso.name,
    }));

    const selectedCursoOptions: Option[] = (form.cursoIds ?? [])
        .map(id => listCursoOptions.find(o => o.value === id))
        .filter(Boolean) as Option[];

    const canSubmit =
        !!form.tipoAgendaId &&
        !!form.startDate &&
        !!form.endDate &&
        Array.isArray(form.cursoIds) &&
        form.cursoIds.length > 0;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!form.tipoAgendaId) {
            Swal.fire('Atenção', 'Selecione o Tipo de agenda.', 'warning');
            return;
        }

        if (!form.cursoIds || form.cursoIds.length === 0) {
            Swal.fire('Atenção', 'Selecione ao menos 1 curso para criar o evento.', 'warning');
            return;
        }

        const payload: CreateAgendaType = {
            ...form,
            contratoId,
            cursoIds: Array.from(new Set(form.cursoIds ?? [])),
            terceiros: selected.map(s => ({
                terceiroId: s.terceiro.id,
                servicoId: s.servicoId ?? null,
                valorServico: (typeof s.valorServico === 'number' ? s.valorServico : null)
            })),
        };

        const ok = await createAgenda(payload);
        if (ok) {
            onCreated?.();
            toggle();
        }
    };

    return (
        <>
            <Modal isOpen={isOpen} toggle={toggle} backdrop="static" size="xl">
                <ModalHeader toggle={toggle}>Adicionar novo evento</ModalHeader>
                <ModalBody>
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label">Tipo de agenda</Label>
                                    <InputGroup>
                                        <Input
                                            className="form-control-alternative"
                                            type="select"
                                            name="tipoAgendaId"
                                            value={form.tipoAgendaId}
                                            onChange={onChange}
                                            required
                                        >
                                            <option value="0">Selecione…</option>
                                            {listTipoAgenda.map(t => (
                                                <option key={t.id} value={t.id}>{t.description}</option>
                                            ))}
                                        </Input><Button
                                                style={{ background: '#585858', color: '#fff' }}
                                                onClick={() => setCreateModalOpen(true)}
                                                type="button"
                                            >
                                                +
                                            </Button></InputGroup>
                                </FormGroup>
                            </Col>

                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="description">Descrição</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="description"
                                        id="description"
                                        value={form.description}
                                        placeholder="Breve descrição"
                                        onChange={onChange}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="location">Local</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="location"
                                        id="location"
                                        value={form.location}
                                        placeholder="Local do evento"
                                        onChange={onChange}
                                    />
                                </FormGroup>
                            </Col>

                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="responsible">Responsável</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="responsible"
                                        id="responsible"
                                        value={form.responsible}
                                        placeholder="Responsável"
                                        onChange={onChange}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md="12">
                                <FormGroup>
                                    <Label className="form-control-label">Cursos</Label>
                                    <Select
                                        options={listCursoOptions}
                                        value={selectedCursoOptions}
                                        isMulti
                                        onChange={(opts: readonly Option[] | null) =>
                                            setForm(prev => ({
                                                ...prev,
                                                cursoIds: (opts ?? []).map(o => o.value),
                                            }))
                                        }
                                        placeholder="Selecione os cursos..."
                                        classNamePrefix="react-select"
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menu: (p) => ({ ...p, zIndex: 9999 }),
                                            menuPortal: (b) => ({ ...b, zIndex: 9999 }),
                                        }}
                                        noOptionsMessage={() => 'Nenhum curso encontrado'}
                                    />

                                    {(!form.cursoIds || form.cursoIds.length === 0) && (
                                        <small className="text-danger d-block mt-1">
                                            Selecione ao menos 1 curso para habilitar a criação do evento.
                                        </small>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label">Fotógrafo</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="select"
                                        onChange={onSelectTerceiro}
                                    >
                                        <option value="">Selecione…</option>
                                        {listTerceiro.map(t => (
                                            <option key={t.id} value={t.id}>
                                                {t.id} – {t.name}
                                            </option>
                                        ))}
                                    </Input>

                                    <div className="mt-3">
                                        {selected.length === 0 && (
                                            <small className="text-muted">Nenhum fotógrafo adicionado.</small>
                                        )}

                                        {selected.map(s => {
                                            const servicoOptions: Option[] = (s.servicos || []).map(item => ({
                                                value: item.servicoId,
                                                label: `${item.servicoName}${item.value ? ` — R$ ${item.value.toFixed(2)}` : ''}`
                                            }));

                                            const selectedServico =
                                                s.servicoId != null
                                                    ? servicoOptions.find(o => o.value === s.servicoId) || null
                                                    : null;

                                            return (
                                                <div
                                                    key={s.terceiro.id}
                                                    className="d-flex align-items-center mb-2"
                                                    style={{ gap: 8 }}
                                                >
                                                    <span className="badge badge-pill badge-primary">
                                                        {s.terceiro.id} - {s.terceiro.name}
                                                    </span>

                                                    <div style={{ minWidth: 260, flex: 1 }}>
                                                        <Select
                                                            options={servicoOptions}
                                                            value={selectedServico}
                                                            onChange={(opt: any) => onChangeServico(s.terceiro.id, opt?.value ?? null)}
                                                            isClearable
                                                            isSearchable
                                                            placeholder={
                                                                servicoOptions.length ? 'Selecione um serviço...' : 'Sem serviços para este fotógrafo'
                                                            }
                                                            isDisabled={servicoOptions.length === 0}
                                                            classNamePrefix="react-select"
                                                            menuPortalTarget={document.body}
                                                            styles={{
                                                                menu: (p) => ({ ...p, zIndex: 9999 }),
                                                                menuPortal: (b) => ({ ...b, zIndex: 9999 }),
                                                            }}
                                                            noOptionsMessage={() => 'Nenhum serviço encontrado'}
                                                        />
                                                    </div>

                                                    <CurrencyInput
                                                        name={`valor-${s.terceiro.id}`}
                                                        value={s.valorServico ?? Number.NaN}
                                                        onValueChange={(v) => {
                                                            setSelected(prev =>
                                                                prev.map(x =>
                                                                    x.terceiro.id === s.terceiro.id
                                                                        ? { ...x, valorServico: Number.isNaN(v) ? null : v }
                                                                        : x
                                                                )
                                                            );
                                                        }}
                                                        placeholder={
                                                            (s.servicoId != null
                                                                ? (() => {
                                                                    const def = s.servicos.find(ss => ss.servicoId === s.servicoId)?.value ?? null;
                                                                    return def != null ? `Valor padrão: R$ ${def.toFixed(2)}` : 'Valor do serviço (opcional)';
                                                                })()
                                                                : 'Selecione um serviço primeiro')
                                                        }
                                                    />

                                                    <Button
                                                        type="button"
                                                        color="danger"
                                                        className="btn btn-sm"
                                                        onClick={() =>
                                                            setSelected(prev => prev.filter(x => x.terceiro.id !== s.terceiro.id))
                                                        }
                                                        title="Remover"
                                                    >
                                                        Remover
                                                    </Button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </FormGroup>
                            </Col>

                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="beca">Beca</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="beca"
                                        id="beca"
                                        value={form.beca}
                                        placeholder="Beca do fotógrafo"
                                        onChange={onChange}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="startDate">Início</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="datetime-local"
                                        name="startDate"
                                        id="startDate"
                                        value={form.startDate}
                                        onChange={onChange}
                                    />
                                </FormGroup>
                            </Col>

                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="endDate">Fim</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="datetime-local"
                                        name="endDate"
                                        id="endDate"
                                        value={form.endDate}
                                        onChange={onChange}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end mt-4">
                            <Button
                                style={{ background: '#585858', color: '#fff' }}
                                type="submit"
                                disabled={!canSubmit}
                                title={!canSubmit ? 'Selecione tipo, período e ao menos 1 curso.' : undefined}
                            >
                                Criar evento
                            </Button>

                            <Button
                                color="danger"
                                className="ml-2"
                                onClick={toggle}
                                type="button"
                            >
                                Cancelar
                            </Button>
                        </div>
                    </Form>
                </ModalBody>
            </Modal>

            <CreateTipoAgendaModal
                isOpen={createModalOpen}
                toggle={() => setCreateModalOpen(false)}
                onCreated={() => getAllTipoAgendaPaged(1, 9, objUserTk.EmpresaId)}
            />
        </>
    );
};

export default CreateAgendaModal;